import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  fetchIndividualHeadToHead, 
  fetchTeamHeadToHead,
  fetchPotentialOpponents,
  fetchMatchupSearchPlayers,
  fetchClubMatchupHighlightGames,
  getPlayerDisplayName
} from '../../services/headToHeadService';
import { supabase } from '../../utils/supabase';
import RecentForm from './RecentForm';
import GameHistoryTable from './GameHistoryTable';
import MatchupStatsHero from './MatchupStatsHero';
import MatchupPlayerPicker from './MatchupPlayerPicker';
import MatchupFilters from './MatchupFilters';
import MatchupHighlights from './MatchupHighlights';
import { getMatchupStatsProps } from './matchupStatsFromResults';
import {
  applyMatchupDateFilters,
  applyMatchupGameTypeFilter,
  DEFAULT_MATCHUP_GAME_TYPE_BUCKETS,
  filterGameGroupsByGameTypeIds,
  filterGamesByMatchupFilters,
  gameTypeIdsFromBuckets,
  normalizeMatchupGender,
  resolveMatchupDateRange,
  resolveSeasonId,
} from '../../utils/matchupFilters';
import {
  buildMatchupHighlights,
  DEFAULT_MATCHUP_HIGHLIGHT_MIN_GAMES,
} from '../../utils/matchupHighlights';

/**
 * Unified Head-to-Head component
 * Consolidates Individual H2H, Team H2H, and "Played With" into one interface
 * @param {string} currentPlayerId - Current player's ID
 * @param {Object|null} selectedClub - Club object (with id property) or null for all clubs
 * @param {string|null} selectedSeason - Optional season ID
 * @param {string|null} initialOpponentId - Optional player ID to pre-select as opponent (from deep link)
 * @param {string|null} initialClubId - Optional club ID to pre-select in the all-clubs filter
 */
const UnifiedHeadToHead = ({
  currentPlayerId,
  selectedClub,
  selectedSeason,
  initialOpponentId = null,
  initialClubId = null,
}) => {
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [error, setError] = useState(null);
  
  // Tag-based selections
  const [teammates, setTeammates] = useState([]); // Array of player objects
  const [opponents, setOpponents] = useState([]); // Array of player objects
  
  // Results state
  const [results, setResults] = useState(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [resultsMode, setResultsMode] = useState(null); // 'teammate-only', 'individual', 'opponent-pair', 'team-vs-team'
  const [resultsDescription, setResultsDescription] = useState('');
  
  // Sort/filter state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showCloseGamesOnly, setShowCloseGamesOnly] = useState(false);

  // Matchup filters
  const [memberClubs, setMemberClubs] = useState([]);
  const [filterClubIds, setFilterClubIds] = useState(() =>
    !selectedClub && initialClubId ? [initialClubId] : []
  );
  const [filterSeasonId, setFilterSeasonId] = useState(
    () => resolveSeasonId(selectedSeason) || ''
  );
  const [seasons, setSeasons] = useState([]);
  const [filterGameTypeBuckets, setFilterGameTypeBuckets] = useState(() => [
    ...DEFAULT_MATCHUP_GAME_TYPE_BUCKETS,
  ]);
  const [playerGender, setPlayerGender] = useState(null);
  const [filterDatePreset, setFilterDatePreset] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [highlightGames, setHighlightGames] = useState([]);
  const [highlightMemberships, setHighlightMemberships] = useState({});
  const [isLoadingHighlights, setIsLoadingHighlights] = useState(false);
  const [highlightMinGames, setHighlightMinGames] = useState(
    DEFAULT_MATCHUP_HIGHLIGHT_MIN_GAMES
  );
  const hasHighlightDataRef = useRef(false);

  // Fetch member clubs for club filter (all-clubs / profile mode)
  useEffect(() => {
    if (selectedClub || !currentPlayerId) {
      setMemberClubs([]);
      return;
    }

    const loadClubs = async () => {
      try {
        const { data: memberships, error: memError } = await supabase
          .from('players_club')
          .select('club_id, clubs:club_id(id, name)')
          .eq('player_id', currentPlayerId)
          .eq('is_active', true);

        if (memError) throw memError;

        const clubs = (memberships || [])
          .map((m) => m.clubs)
          .filter(Boolean)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        setMemberClubs(clubs);
      } catch (err) {
        console.error('Error loading member clubs:', err);
      }
    };

    loadClubs();
  }, [currentPlayerId, selectedClub]);

  useEffect(() => {
    if (selectedClub || !initialClubId || memberClubs.length === 0) return;
    const isMember = memberClubs.some((club) => club.id === initialClubId);
    setFilterClubIds((current) => {
      if (isMember) {
        return current.length === 1 && current[0] === initialClubId ? current : [initialClubId];
      }
      return current.filter((id) => id !== initialClubId);
    });
  }, [selectedClub, initialClubId, memberClubs]);

  useEffect(() => {
    if (!currentPlayerId) {
      setPlayerGender(null);
      return;
    }

    let cancelled = false;
    const loadGender = async () => {
      try {
        const { data, error: genderError } = await supabase
          .from('players')
          .select('gender')
          .eq('id', currentPlayerId)
          .maybeSingle();
        if (genderError) throw genderError;
        if (!cancelled) setPlayerGender(normalizeMatchupGender(data?.gender));
      } catch (err) {
        console.error('Error loading matchup player gender:', err);
        if (!cancelled) setPlayerGender(null);
      }
    };

    loadGender();
    return () => {
      cancelled = true;
    };
  }, [currentPlayerId]);

  const seasonsClubId = selectedClub?.id || (filterClubIds.length === 1 ? filterClubIds[0] : '');
  const resolvedDates = useMemo(
    () => resolveMatchupDateRange(filterDatePreset, filterDateFrom, filterDateTo),
    [filterDatePreset, filterDateFrom, filterDateTo]
  );
  const filterGameTypeIds = useMemo(
    () => gameTypeIdsFromBuckets(filterGameTypeBuckets, playerGender),
    [filterGameTypeBuckets, playerGender]
  );

  useEffect(() => {
    if (!seasonsClubId) {
      setSeasons([]);
      setFilterSeasonId('');
      return;
    }

    let cancelled = false;
    const loadSeasons = async () => {
      try {
        const { data, error: seasonsError } = await supabase
          .from('seasons')
          .select('id, name, start_date')
          .eq('club_id', seasonsClubId)
          .order('start_date', { ascending: false });
        if (seasonsError) throw seasonsError;
        if (cancelled) return;
        const nextSeasons = data || [];
        setSeasons(nextSeasons);
        setFilterSeasonId((current) =>
          nextSeasons.some((season) => season.id === current) ? current : ''
        );
      } catch (err) {
        console.error('Error loading matchup seasons:', err);
        if (!cancelled) {
          setSeasons([]);
          setFilterSeasonId('');
        }
      }
    };

    loadSeasons();
    return () => {
      cancelled = true;
    };
  }, [seasonsClubId]);

  // Fetch available players
  useEffect(() => {
    const loadPlayers = async () => {
      if (!currentPlayerId) {
        setAvailablePlayers([]);
        return;
      }

      setIsLoadingPlayers(true);
      setError(null);
      try {
        let playersData = [];
        
        if (selectedClub) {
          // Single club mode
          playersData = await fetchPotentialOpponents(currentPlayerId, selectedClub.id);
        } else {
          // All clubs mode - get all clubs the player is a member of
          const { data: playerClubs, error: clubsError } = await supabase
            .from('players_club')
            .select('club_id')
            .eq('player_id', currentPlayerId)
            .eq('is_active', true);

          if (clubsError) throw clubsError;

          if (!playerClubs || playerClubs.length === 0) {
            setAvailablePlayers([]);
            setIsLoadingPlayers(false);
            return;
          }

          const clubIds = playerClubs.map(pc => pc.club_id);
          playersData = await fetchMatchupSearchPlayers(currentPlayerId, clubIds);
        }
        
        setAvailablePlayers(playersData);
      } catch (err) {
        console.error('Error loading players:', err);
        setError('Failed to load players');
      } finally {
        setIsLoadingPlayers(false);
      }
    };

    loadPlayers();
  }, [currentPlayerId, selectedClub]);

  // Auto-select opponent from deep-link param once players are loaded
  useEffect(() => {
    if (!initialOpponentId || isLoadingPlayers || availablePlayers.length === 0) return;
    // Only auto-select on initial load (don't override manual selections)
    if (opponents.length > 0 || teammates.length > 0) return;
    const match = availablePlayers.find((p) => p.id === initialOpponentId);
    if (match) setOpponents([match]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpponentId, availablePlayers, isLoadingPlayers]);

  useEffect(() => {
    if (!selectedClub?.id || !currentPlayerId) {
      setHighlightGames([]);
      setHighlightMemberships({});
      hasHighlightDataRef.current = false;
      return;
    }

    let cancelled = false;
    const loadHighlights = async () => {
      if (!hasHighlightDataRef.current) setIsLoadingHighlights(true);
      try {
        const { games, hasActiveClubMembershipByPlayerId } = await fetchClubMatchupHighlightGames(
          currentPlayerId,
          selectedClub.id,
          {
            seasonId: filterSeasonId || null,
            dateFrom: resolvedDates.dateFrom || null,
            dateTo: resolvedDates.dateTo || null,
            gameTypeIds: filterGameTypeIds,
          }
        );
        if (cancelled) return;
        setHighlightGames(games);
        setHighlightMemberships(hasActiveClubMembershipByPlayerId);
        hasHighlightDataRef.current = games.length > 0;
      } catch (err) {
        console.error('Error loading matchup highlights:', err);
        if (!cancelled) {
          setHighlightGames([]);
          setHighlightMemberships({});
        }
      } finally {
        if (!cancelled) setIsLoadingHighlights(false);
      }
    };

    loadHighlights();
    return () => {
      cancelled = true;
    };
  }, [
    currentPlayerId,
    selectedClub?.id,
    filterSeasonId,
    resolvedDates.dateFrom,
    resolvedDates.dateTo,
    filterGameTypeIds,
  ]);

  const highlightLists = useMemo(
    () =>
      buildMatchupHighlights(highlightGames, {
        currentPlayerId,
        minGames: highlightMinGames,
        gameTypeIds: filterGameTypeIds,
        hasActiveClubMembershipByPlayerId: highlightMemberships,
      }),
    [
      highlightGames,
      currentPlayerId,
      highlightMinGames,
      filterGameTypeIds,
      highlightMemberships,
    ]
  );

  // Filter available players to exclude already selected ones and current player
  const getAvailableTeammates = () => {
    return availablePlayers.filter(player => 
      !teammates.find(t => t.id === player.id) &&
      player.id !== currentPlayerId
    );
  };

  const getAvailableOpponents = () => {
    return availablePlayers.filter(player => 
      !opponents.find(o => o.id === player.id) &&
      player.id !== currentPlayerId
    );
  };

  // Determine results mode and fetch data
  const fetchResults = async () => {
    if (!currentPlayerId) return;

    const seasonId = filterSeasonId || null;
    const dateOpts = {
      dateFrom: resolvedDates.dateFrom || null,
      dateTo: resolvedDates.dateTo || null,
      gameTypeIds: filterGameTypeIds,
    };
    const matchupFilterParams = {
      gameTypeIds: filterGameTypeIds,
      dateFrom: dateOpts.dateFrom,
      dateTo: dateOpts.dateTo,
    };

    // Get club IDs to query (fixed club, filtered clubs, or all member clubs)
    let clubIds = [];
    if (selectedClub) {
      clubIds = [selectedClub.id];
    } else if (filterClubIds.length > 0) {
      clubIds = filterClubIds;
    } else {
      const { data: playerClubs, error: clubsError } = await supabase
        .from('players_club')
        .select('club_id')
        .eq('player_id', currentPlayerId)
        .eq('is_active', true);

      if (clubsError) {
        setError('Failed to load clubs');
        setIsLoadingResults(false);
        return;
      }

      if (!playerClubs || playerClubs.length === 0) {
        setError('You are not a member of any clubs');
        setIsLoadingResults(false);
        return;
      }

      clubIds = playerClubs.map((pc) => pc.club_id);
    }

    const descriptionSuffix =
      selectedClub || filterClubIds.length > 0 ? '' : ' across all clubs';

    setIsLoadingResults(true);
    setError(null);
    setResults(null);
    setResultsMode(null);
    setResultsDescription('');

    try {
      // Determine mode based on selections
      const hasTeammate = teammates.length > 0;
      const opponentCount = opponents.length;

      if (hasTeammate && opponentCount === 0) {
        // Teammate only → games played with that teammate
        setResultsMode('teammate-only');
        const teammate = teammates[0];
        setResultsDescription(`Showing games played with ${getPlayerDisplayName(teammate)}${descriptionSuffix}`);
        
        // Fetch games where both players were on the same team
        let query = supabase
          .from('players_session_games')
          .select(`
            *,
            session_games!inner(
              id,
              session_id,
              club_id,
              season_id,
              created_at,
              team_1_score,
              team_2_score,
              winning_team,
              game_type_id
            )
          `)
          .in('player_id', [currentPlayerId, teammate.id]);
        
        // Filter by club(s)
        if (selectedClub) {
          query = query.eq('session_games.club_id', selectedClub.id);
        } else {
          query = query.in('session_games.club_id', clubIds);
        }
        
        if (seasonId) {
          query = query.eq('session_games.season_id', seasonId);
        }
        query = applyMatchupDateFilters(query, dateOpts.dateFrom, dateOpts.dateTo);
        query = applyMatchupGameTypeFilter(query, filterGameTypeIds);

        const { data: playerGames, error: gamesError } = await query;
        if (gamesError) throw gamesError;
        
        // Group by game and find games where both players were on the same team
        const gameGroups = {};
        playerGames.forEach(game => {
          const gameId = game.session_game_id;
          if (!gameGroups[gameId]) {
            gameGroups[gameId] = [];
          }
          gameGroups[gameId].push(game);
        });
        
        // Get all player IDs from partnership games for fetching player details
        const allGameIds = Object.values(gameGroups)
          .filter(group => group.length === 2 && group[0].team_number === group[1].team_number)
          .map(group => group[0].session_games.id);
        
        // Fetch all players for these games
        const { data: allGamePlayers, error: playersError } = await supabase
          .from('players_session_games')
          .select(`
            player_id,
            team_number,
            session_game_id,
            players:player_id(
              id,
              first_name,
              last_name,
              gender,
              is_guest,
              is_active,
              merged_at,
              guest_merge_declined_at
            )
          `)
          .in('session_game_id', allGameIds);
        
        if (playersError) throw playersError;
        
        // Group players by game ID
        const playersByGame = {};
        (allGamePlayers || []).forEach(gp => {
          const gameId = gp.session_game_id;
          if (!playersByGame[gameId]) {
            playersByGame[gameId] = [];
          }
          playersByGame[gameId].push(gp);
        });
        
        // Filter to games where both players were on the same team and enrich with player data
        const partnershipGames = Object.values(gameGroups)
          .filter(group => group.length === 2 && group[0].team_number === group[1].team_number)
          .map(group => {
            const game = group[0].session_games;
            const ourTeam = group[0].team_number;
            const isWin = game.winning_team === ourTeam;
            const opponentTeam = ourTeam === 1 ? 2 : 1;
            
            // Get all players for this game
            const gamePlayers = playersByGame[game.id] || [];
            const ourTeamPlayers = gamePlayers.filter(p => p.team_number === ourTeam);
            const opponentTeamPlayers = gamePlayers.filter(p => p.team_number === opponentTeam);
            
            // Format player names
            const formatPlayerName = (player) => {
              if (!player.players) return null;
              return `${player.players.first_name} ${player.players.last_name}`;
            };
            
            const ourTeamNames = ourTeamPlayers
              .map(p => formatPlayerName(p))
              .filter(Boolean);
            const opponentTeamNames = opponentTeamPlayers
              .map(p => formatPlayerName(p))
              .filter(Boolean);
            
            // Create player objects for GameCard (expects these properties directly on game object)
            const createPlayerObject = (playerData) => {
              if (!playerData?.players) return null;
              return {
                id: playerData.players.id,
                name: formatPlayerName(playerData),
                first_name: playerData.players.first_name,
                last_name: playerData.players.last_name,
                gender: playerData.players.gender
              };
            };
            
            return {
              gameId: game.id,
              date: game.created_at,
              created_at: game.created_at,
              sessionId: game.session_id,
              session_id: game.session_id,
              club_id: game.club_id,
              clubId: game.club_id,
              team1Score: game.team_1_score,
              team2Score: game.team_2_score,
              // GameCard expects a score string in format "team1-team2"
              score: `${game.team_1_score}-${game.team_2_score}`,
              // For team games, GameCard needs to know which team was "team1" (our team)
              team1PlayersInGameTeamNumber: ourTeam,
              winningTeam: game.winning_team,
              isWin,
              player1Result: isWin ? 'win' : 'loss',
              team1Result: isWin ? 'win' : 'loss',
              scoreDifference: Math.abs(game.team_1_score - game.team_2_score),
              isCloseGame: Math.abs(game.team_1_score - game.team_2_score) <= 3,
              pairType: group[0].pair_type || 'mixed',
              pair_type: group[0].pair_type,
              gameTypeId: game.game_type_id,
              // GameCard expects these properties directly on the game object for team display
              team1Player1: createPlayerObject(ourTeamPlayers[0]),
              team1Player2: createPlayerObject(ourTeamPlayers[1]),
              team2Player1: createPlayerObject(opponentTeamPlayers[0]),
              team2Player2: createPlayerObject(opponentTeamPlayers[1])
            };
          });

        const filteredPartnershipGames = filterGamesByMatchupFilters(
          partnershipGames,
          matchupFilterParams
        );

        const wins = filteredPartnershipGames.filter((g) => g.isWin).length;
        const losses = filteredPartnershipGames.filter((g) => !g.isWin).length;
        const totalGames = filteredPartnershipGames.length;
        const winRate = totalGames > 0 ? (wins / totalGames) * 100 : 0;

        const recentResults = filteredPartnershipGames
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 5)
          .map(g => g.isWin ? 'win' : 'loss');
        
        setResults({
          mode: 'teammate-only',
          teammate: teammate,
          totalGames,
          wins,
          losses,
          winRate,
          games: filteredPartnershipGames,
          recentResults,
        });
      } else if (!hasTeammate && opponentCount === 1) {
        // One opponent → individual H2H
        setResultsMode('individual');
        const opponent = opponents[0];
        setResultsDescription(`Showing games vs ${getPlayerDisplayName(opponent)}${descriptionSuffix}`);
        
        // Fetch individual H2H for single club or all clubs
        let h2hData;
        if (selectedClub || clubIds.length === 1) {
          const clubId = selectedClub?.id ?? clubIds[0];
          h2hData = await fetchIndividualHeadToHead(
            currentPlayerId,
            opponent.id,
            clubId,
            seasonId,
            'all',
            dateOpts
          );
        } else {
          const allResults = await Promise.all(
            clubIds.map((clubId) =>
              fetchIndividualHeadToHead(
                currentPlayerId,
                opponent.id,
                clubId,
                seasonId,
                'all',
                dateOpts
              )
            )
          );

          // Combine results from all clubs
          let combinedGames = [];
          let totalWins = 0;
          let totalLosses = 0;
          let totalDraws = 0;
          let totalGames = 0;

          allResults.forEach(result => {
            if (result && result.games) {
              combinedGames = [...combinedGames, ...result.games];
              totalGames += result.totalGames || 0;
              
              // For individual H2H, determine which player is the current user
              const isCurrentUserPlayer1 = result.player1Id === currentPlayerId;
              if (isCurrentUserPlayer1) {
                totalWins += result.player1Wins || 0;
                totalLosses += result.player2Wins || 0;
              } else {
                totalWins += result.player2Wins || 0;
                totalLosses += result.player1Wins || 0;
              }
            }
          });

          const winRate = totalGames > 0 ? (totalWins / totalGames) * 100 : 0;

          h2hData = {
            player1Id: currentPlayerId,
            player2Id: opponent.id,
            totalGames,
            player1Wins: totalWins,
            player2Wins: totalLosses,
            player1WinRate: winRate,
            player2WinRate: 100 - winRate,
            games: combinedGames,
            wins: totalWins,
            losses: totalLosses,
            draws: totalDraws,
            winRate: winRate
          };
        }
        
        setResults({
          mode: 'individual',
          opponent: opponent,
          ...h2hData
        });
      } else if (!hasTeammate && opponentCount === 2) {
        // Two opponents, no teammate → games against that pair
        setResultsMode('opponent-pair');
        const [opp1, opp2] = opponents;
        setResultsDescription(`Matchup vs ${getPlayerDisplayName(opp1)} & ${getPlayerDisplayName(opp2)}${descriptionSuffix}`);
        
        // Use team H2H but with current player as solo team
        let h2hData;
        if (selectedClub || clubIds.length === 1) {
          const clubId = selectedClub?.id ?? clubIds[0];
          const rawH2hData = await fetchTeamHeadToHead(
            currentPlayerId,
            currentPlayerId,
            opp1.id,
            opp2.id,
            clubId,
            seasonId,
            'all',
            dateOpts
          );
          
          // Fix teammate display: Find actual teammates from game data
          if (rawH2hData?.games) {
            const gameIds = rawH2hData.games.map(g => g.gameId);
            const { data: gamePlayers, error: playersError } = await supabase
              .from('players_session_games')
              .select(`
                player_id,
                team_number,
                session_game_id,
                players:player_id(
                  id,
                  first_name,
                  last_name,
                  gender
                )
              `)
              .in('session_game_id', gameIds);

            if (!playersError && gamePlayers) {
              // Group players by game
              const playersByGame = {};
              gamePlayers.forEach(gp => {
                const gameId = gp.session_game_id;
                if (!playersByGame[gameId]) {
                  playersByGame[gameId] = [];
                }
                playersByGame[gameId].push(gp);
              });

              // Fix team1Player2 for each game
              // In opponent-pair mode, team1Player1 is always the current player, but team1Player2 might be wrong
              // Exclude opponents from teammate search
              const opponentIds = [opp1.id, opp2.id];
              rawH2hData.games.forEach(game => {
                // Always fix if team1Player1 is the current player (which it should be in opponent-pair mode)
                if (game.team1Player1?.id === currentPlayerId) {
                  const gamePlayers = playersByGame[game.gameId] || [];
                  const currentPlayerTeam = gamePlayers.find(p => p.player_id === currentPlayerId)?.team_number;
                  
                  if (currentPlayerTeam) {
                    // Find teammate: same team as current player, but NOT one of the opponents
                    const actualTeammate = gamePlayers.find(
                      p => p.player_id !== currentPlayerId && 
                           !opponentIds.includes(p.player_id) &&
                           p.team_number === currentPlayerTeam &&
                           p.players // Make sure we have player data
                    );
                    
                    if (actualTeammate?.players) {
                      game.team1Player2 = {
                        id: actualTeammate.players.id,
                        name: `${actualTeammate.players.first_name} ${actualTeammate.players.last_name}`,
                        first_name: actualTeammate.players.first_name,
                        last_name: actualTeammate.players.last_name,
                        gender: actualTeammate.players.gender
                      };
                    } else {
                      // If no teammate found, log for debugging
                      console.warn(`No teammate found for game ${game.gameId}. Current player team: ${currentPlayerTeam}, Game players:`, gamePlayers.map(p => ({ id: p.player_id, team: p.team_number, name: p.players?.first_name + ' ' + p.players?.last_name })));
                    }
                  }
                }
              });
            }
          }
          
          h2hData = rawH2hData;
        } else {
          // All clubs mode - fetch and combine results
          // Note: We pass currentPlayerId twice because fetchTeamHeadToHead expects 4 players
          // but in opponent-pair mode, the current player is solo. We'll fix the teammate display later.
          const allResults = await Promise.all(
            clubIds.map((clubId) =>
              fetchTeamHeadToHead(
                currentPlayerId,
                currentPlayerId,
                opp1.id,
                opp2.id,
                clubId,
                seasonId,
                'all',
                dateOpts
              )
            )
          );

          // Combine results from all clubs
          let combinedGames = [];
          const gameIdsSeen = new Set(); // Track unique games to avoid duplicates

          allResults.forEach(result => {
            if (result && result.games) {
              result.games.forEach(game => {
                // Only add unique games (by gameId)
                if (!gameIdsSeen.has(game.gameId)) {
                  gameIdsSeen.add(game.gameId);
                  
                  // Fix team display: In opponent-pair mode, we passed currentPlayerId twice
                  // but the actual teammate is in the game. Find the real teammate.
                  if (game.team1Player1?.id === currentPlayerId && game.team1Player2?.id === currentPlayerId) {
                    // This is the case where we passed the same player twice
                    // We need to find the actual teammate from the game data
                    // The teammate should be on the same team as currentPlayerId
                    // We'll need to fetch this from the actual game players
                    // For now, we'll leave it as is and fix it in post-processing
                  }
                  
                  combinedGames.push(game);
                }
              });
            }
          });

          // Post-process games to fix teammate display for opponent-pair mode
          // We need to fetch the actual teammates from the database
          const gameIdsToFix = combinedGames
            .filter(g => g.team1Player1?.id === currentPlayerId && g.team1Player2?.id === currentPlayerId)
            .map(g => g.gameId);

          if (gameIdsToFix.length > 0) {
            // Fetch actual players for these games
            const { data: gamePlayers, error: playersError } = await supabase
              .from('players_session_games')
              .select(`
                player_id,
                team_number,
                session_game_id,
                players:player_id(
                  id,
                  first_name,
                  last_name,
                  gender
                )
              `)
              .in('session_game_id', gameIdsToFix);

            if (!playersError && gamePlayers) {
              // Group players by game
              const playersByGame = {};
              gamePlayers.forEach(gp => {
                const gameId = gp.session_game_id;
                if (!playersByGame[gameId]) {
                  playersByGame[gameId] = [];
                }
                playersByGame[gameId].push(gp);
              });

              // Fix team1Player2 for each game
              // In opponent-pair mode, team1Player1 is always the current player, but team1Player2 might be wrong
              // Exclude opponents from teammate search
              const opponentIds = [opp1.id, opp2.id];
              combinedGames.forEach(game => {
                // Always fix if team1Player1 is the current player (which it should be in opponent-pair mode)
                if (game.team1Player1?.id === currentPlayerId) {
                  const gamePlayers = playersByGame[game.gameId] || [];
                  const currentPlayerTeam = gamePlayers.find(p => p.player_id === currentPlayerId)?.team_number;
                  
                  if (currentPlayerTeam) {
                    // Find teammate: same team as current player, but NOT one of the opponents
                    const actualTeammate = gamePlayers.find(
                      p => p.player_id !== currentPlayerId && 
                           !opponentIds.includes(p.player_id) &&
                           p.team_number === currentPlayerTeam &&
                           p.players // Make sure we have player data
                    );
                    
                    if (actualTeammate?.players) {
                      game.team1Player2 = {
                        id: actualTeammate.players.id,
                        name: `${actualTeammate.players.first_name} ${actualTeammate.players.last_name}`,
                        first_name: actualTeammate.players.first_name,
                        last_name: actualTeammate.players.last_name,
                        gender: actualTeammate.players.gender
                      };
                    } else {
                      // If no teammate found, log for debugging
                      console.warn(`No teammate found for game ${game.gameId}. Current player team: ${currentPlayerTeam}, Game players:`, gamePlayers.map(p => ({ id: p.player_id, team: p.team_number, name: p.players?.first_name + ' ' + p.players?.last_name })));
                    }
                  }
                }
              });
            }
          }

          // Calculate stats from combined games
          let totalWins = 0;
          let totalLosses = 0;
          let totalDraws = 0;

          combinedGames.forEach(game => {
            const isWin = game.team1Result === 'win' || (game.winningTeam === game.team1PlayersInGameTeamNumber);
            if (isWin) {
              totalWins++;
            } else if (game.team1Result === 'loss' || (game.winningTeam !== game.team1PlayersInGameTeamNumber && game.winningTeam !== null)) {
              totalLosses++;
            } else {
              totalDraws++;
            }
          });

          const totalGames = combinedGames.length;
          const winRate = totalGames > 0 ? (totalWins / totalGames) * 100 : 0;

          h2hData = {
            team1: { player1Id: currentPlayerId, player2Id: currentPlayerId },
            team2: { player3Id: opp1.id, player4Id: opp2.id },
            totalGames,
            team1Wins: totalWins,
            team2Wins: totalLosses,
            team1WinRate: winRate,
            team2WinRate: 100 - winRate,
            games: combinedGames,
            wins: totalWins,
            losses: totalLosses,
            draws: totalDraws,
            winRate: winRate
          };
        }
        
        setResults({
          mode: 'opponent-pair',
          opponents: [opp1, opp2],
          ...h2hData
        });
      } else if (hasTeammate && (opponentCount === 1 || opponentCount === 2)) {
        // Teammate + opponents → team vs team stats
        setResultsMode('team-vs-team');
        const teammate = teammates[0];
        const opp1 = opponents[0];
        const opp2 = opponents[1];
        
        if (opponentCount === 1) {
          setResultsDescription(`Showing [You + ${getPlayerDisplayName(teammate)}] vs [${getPlayerDisplayName(opp1)}]${descriptionSuffix}`);
          
          // For 1 opponent, we need to find games where our team (You + teammate) played against that opponent
          // and their actual teammate in each game
          let query = supabase
            .from('players_session_games')
            .select(`
              *,
              session_games!inner(
                id,
                session_id,
                club_id,
                season_id,
                created_at,
                team_1_score,
                team_2_score,
                winning_team,
                game_type_id
              )
            `)
            .in('player_id', [currentPlayerId, teammate.id, opp1.id]);
          
          // Filter by club(s)
          if (selectedClub) {
            query = query.eq('session_games.club_id', selectedClub.id);
          } else {
            query = query.in('session_games.club_id', clubIds);
          }
          
          if (seasonId) {
            query = query.eq('session_games.season_id', seasonId);
          }
          query = applyMatchupDateFilters(query, dateOpts.dateFrom, dateOpts.dateTo);
          query = applyMatchupGameTypeFilter(query, filterGameTypeIds);

          const { data: playerGames, error: gamesError } = await query;
          if (gamesError) throw gamesError;

          const gameGroups = {};
          playerGames.forEach(game => {
            const gameId = game.session_game_id;
            if (!gameGroups[gameId]) {
              gameGroups[gameId] = [];
            }
            gameGroups[gameId].push(game);
          });

          let teamH2hGames = Object.values(gameGroups)
            .filter(group => {
              const playerIds = group.map(g => g.player_id);
              const hasOurTeam = playerIds.includes(currentPlayerId) && playerIds.includes(teammate.id);
              const hasOpponent = playerIds.includes(opp1.id);
              
              if (!hasOurTeam || !hasOpponent) return false;
              
              // Check that our team was on the same team (same team_number)
              const currentPlayerGame = group.find(g => g.player_id === currentPlayerId);
              const teammateGame = group.find(g => g.player_id === teammate.id);
              if (!currentPlayerGame || !teammateGame) return false;
              if (currentPlayerGame.team_number !== teammateGame.team_number) return false;
              
              // Opponent must be on the opposite team
              const opponentGame = group.find(g => g.player_id === opp1.id);
              if (!opponentGame) return false;
              if (opponentGame.team_number === currentPlayerGame.team_number) return false;
              
              return true;
            });

          teamH2hGames = filterGameGroupsByGameTypeIds(teamH2hGames, filterGameTypeIds);

          const allGameIds = teamH2hGames.map(group => group[0].session_games.id);
          
          // Fetch all players for these games
          const { data: allGamePlayers, error: playersError } = await supabase
            .from('players_session_games')
            .select(`
              player_id,
              team_number,
              session_game_id,
              players:player_id(
                id,
                first_name,
                last_name,
                gender,
                is_guest,
                is_active,
                merged_at,
                guest_merge_declined_at
              )
            `)
            .in('session_game_id', allGameIds);
          
          if (playersError) throw playersError;
          
          // Group players by game ID
          const playersByGame = {};
          (allGamePlayers || []).forEach(gp => {
            const gameId = gp.session_game_id;
            if (!playersByGame[gameId]) {
              playersByGame[gameId] = [];
            }
            playersByGame[gameId].push(gp);
          });
          
          let games = teamH2hGames.map(group => {
            const game = group[0].session_games;
            const currentPlayerGame = group.find(g => g.player_id === currentPlayerId);
            const ourTeam = currentPlayerGame.team_number;
            const opponentTeam = ourTeam === 1 ? 2 : 1;
            const isWin = game.winning_team === ourTeam;
            
            // Get all players for this game
            const gamePlayers = playersByGame[game.id] || [];
            const ourTeamPlayers = gamePlayers.filter(p => p.team_number === ourTeam);
            const opponentTeamPlayers = gamePlayers.filter(p => p.team_number === opponentTeam);
            
            // Format player names
            const formatPlayerName = (player) => {
              if (!player.players) return null;
              return `${player.players.first_name} ${player.players.last_name}`;
            };
            
            // Create player objects for GameCard
            const createPlayerObject = (playerData) => {
              if (!playerData?.players) return null;
              return {
                id: playerData.players.id,
                name: formatPlayerName(playerData),
                first_name: playerData.players.first_name,
                last_name: playerData.players.last_name,
                gender: playerData.players.gender
              };
            };
            
            return {
              gameId: game.id,
              date: game.created_at,
              created_at: game.created_at,
              sessionId: game.session_id,
              session_id: game.session_id,
              club_id: game.club_id,
              clubId: game.club_id,
              team1Score: game.team_1_score,
              team2Score: game.team_2_score,
              score: `${game.team_1_score}-${game.team_2_score}`,
              team1PlayersInGameTeamNumber: ourTeam,
              winningTeam: game.winning_team,
              isWin,
              player1Result: isWin ? 'win' : 'loss',
              team1Result: isWin ? 'win' : 'loss',
              scoreDifference: Math.abs(game.team_1_score - game.team_2_score),
              isCloseGame: Math.abs(game.team_1_score - game.team_2_score) <= 3,
              pairType: group[0].pair_type || 'mixed',
              pair_type: group[0].pair_type,
              gameTypeId: game.game_type_id,
              team1Player1: createPlayerObject(ourTeamPlayers[0]),
              team1Player2: createPlayerObject(ourTeamPlayers[1]),
              team2Player1: createPlayerObject(opponentTeamPlayers[0]),
              team2Player2: createPlayerObject(opponentTeamPlayers[1])
            };
          });

          games = filterGamesByMatchupFilters(games, matchupFilterParams);

          const team1Wins = games.filter(g => g.isWin).length;
          const team2Wins = games.filter(g => !g.isWin).length;
          const totalGames = games.length;
          const team1WinRate = totalGames > 0 ? (team1Wins / totalGames) * 100 : 0;
          
          // Create recent results
          const recentResults = games
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5)
            .map(g => g.isWin ? 'win' : 'loss');
          
          setResults({
            mode: 'team-vs-team',
            yourTeam: [currentPlayerId, teammate.id],
            opponentTeam: [opp1.id],
            team1Wins,
            team2Wins,
            totalGames,
            team1WinRate,
            team2WinRate: 100 - team1WinRate,
            games,
            recentResults
          });
        } else {
          setResultsDescription(`Showing [You + ${getPlayerDisplayName(teammate)}] vs [${getPlayerDisplayName(opp1)} & ${getPlayerDisplayName(opp2)}]${descriptionSuffix}`);
          
          let h2hData;
          if (selectedClub || clubIds.length === 1) {
            const clubId = selectedClub?.id ?? clubIds[0];
            h2hData = await fetchTeamHeadToHead(
              currentPlayerId,
              teammate.id,
              opp1.id,
              opp2.id,
              clubId,
              seasonId,
              'all',
              dateOpts
            );
          } else {
            const allResults = await Promise.all(
              clubIds.map((clubId) =>
                fetchTeamHeadToHead(
                  currentPlayerId,
                  teammate.id,
                  opp1.id,
                  opp2.id,
                  clubId,
                  seasonId,
                  'all',
                  dateOpts
                )
              )
            );

            // Combine results from all clubs
            let combinedGames = [];
            const gameIdsSeen = new Set(); // Track unique games to avoid duplicates

            allResults.forEach(result => {
              if (result && result.games) {
                result.games.forEach(game => {
                  // Only add unique games (by gameId)
                  if (!gameIdsSeen.has(game.gameId)) {
                    gameIdsSeen.add(game.gameId);
                    combinedGames.push(game);
                  }
                });
              }
            });

            // Calculate stats from combined games
            let totalWins = 0;
            let totalLosses = 0;
            let totalDraws = 0;

            combinedGames.forEach(game => {
              const isWin = game.team1Result === 'win' || (game.winningTeam === game.team1PlayersInGameTeamNumber);
              if (isWin) {
                totalWins++;
              } else if (game.team1Result === 'loss' || (game.winningTeam !== game.team1PlayersInGameTeamNumber && game.winningTeam !== null)) {
                totalLosses++;
              } else {
                totalDraws++;
              }
            });

            const totalGames = combinedGames.length;
            const winRate = totalGames > 0 ? (totalWins / totalGames) * 100 : 0;

            h2hData = {
              team1: { player1Id: currentPlayerId, player2Id: teammate.id },
              team2: { player3Id: opp1.id, player4Id: opp2.id },
              totalGames,
              team1Wins: totalWins,
              team2Wins: totalLosses,
              team1WinRate: winRate,
              team2WinRate: 100 - winRate,
              games: combinedGames,
              wins: totalWins,
              losses: totalLosses,
              draws: totalDraws,
              winRate: winRate
            };
          }
          
          setResults({
            mode: 'team-vs-team',
            yourTeam: [currentPlayerId, teammate.id],
            opponentTeam: [opp1.id, opp2.id],
            ...h2hData
          });
        }
      } else {
        // Invalid combination
        setError('Invalid selection. Please select a teammate, opponent(s), or both.');
        setIsLoadingResults(false);
        return;
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(`Failed to load results: ${err.message || err}`);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Auto-fetch when selections change (after a short delay)
  useEffect(() => {
    const hasSelections = teammates.length > 0 || opponents.length > 0;
    if (!hasSelections) {
      setResults(null);
      setResultsMode(null);
      setResultsDescription('');
      return;
    }

    // Debounce the fetch
    const timer = setTimeout(() => {
      fetchResults();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    teammates,
    opponents,
    currentPlayerId,
    selectedClub,
    filterSeasonId,
    filterClubIds,
    filterGameTypeIds,
    filterDatePreset,
    filterDateFrom,
    filterDateTo,
  ]);

  // Helper functions
  const getCurrentPlayerName = () => {
    const currentPlayer = availablePlayers.find(p => p.id === currentPlayerId);
    return currentPlayer ? getPlayerDisplayName(currentPlayer) : 'You';
  };

  const handleAddTeammate = (player) => {
    if (teammates.length >= 1) {
      setError('You can only select one teammate at a time');
      return;
    }
    if (teammates.find(t => t.id === player.id)) return;
    setTeammates([...teammates, player]);
    setError(null);
  };

  const handleRemoveTeammate = () => {
    setTeammates([]);
  };

  const handleAddOpponent = (player) => {
    if (opponents.length >= 2) {
      setError('You can only select up to 2 opponents at a time');
      return;
    }
    if (opponents.find(o => o.id === player.id)) return;
    setOpponents([...opponents, player]);
    setError(null);
  };

  const handleRemoveOpponent = (playerId) => {
    setOpponents(opponents.filter(o => o.id !== playerId));
  };

  const handleSelectHighlightTeammate = (player) => {
    setOpponents([]);
    setTeammates([player]);
    setError(null);
  };

  const handleSelectHighlightOpponent = (player) => {
    setTeammates([]);
    setOpponents([player]);
    setError(null);
  };

  // Sort and filter games
  const getSortedAndFilteredGames = (games) => {
    if (!games) return [];
    
    let filteredGames = games;
    
    if (showCloseGamesOnly) {
      filteredGames = games.filter(game => game.isCloseGame);
    }
    
    return filteredGames.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date || a.created_at) - new Date(b.date || b.created_at);
          break;
        case 'score':
          comparison = (a.scoreDifference || 0) - (b.scoreDifference || 0);
          break;
        case 'result':
          const aResult = a.player1Result || a.team1Result || (a.winningTeam === 1 ? 'win' : 'loss');
          const bResult = b.player1Result || b.team1Result || (b.winningTeam === 1 ? 'win' : 'loss');
          comparison = aResult === 'win' ? -1 : 1;
          if (bResult === 'win') comparison = -comparison;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const hasSelections = teammates.length > 0 || opponents.length > 0;
  const statsProps =
    results && !isLoadingResults
      ? getMatchupStatsProps(results, resultsMode, currentPlayerId, getPlayerDisplayName)
      : null;

  return (
    <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-3 sm:p-4 md:p-6">
      {error && (
        <div className="bg-danger-light border border-danger text-danger-dark px-3 py-2 rounded text-sm mb-3">
          {error}
        </div>
      )}

      <MatchupFilters
        showClubFilter={!selectedClub}
        clubs={memberClubs}
        clubIds={filterClubIds}
        onClubIdsChange={(nextClubIds) => {
          setFilterClubIds(nextClubIds);
          if (nextClubIds.length !== 1) setFilterSeasonId('');
        }}
        lockedClubName={selectedClub?.name}
        seasons={seasons}
        seasonId={filterSeasonId}
        onSeasonChange={setFilterSeasonId}
        gameTypeBuckets={filterGameTypeBuckets}
        onGameTypeBucketsChange={setFilterGameTypeBuckets}
        datePreset={filterDatePreset}
        onDatePresetChange={setFilterDatePreset}
        dateFrom={filterDateFrom}
        onDateFromChange={setFilterDateFrom}
        dateTo={filterDateTo}
        onDateToChange={setFilterDateTo}
        onClearDates={() => {
          setFilterDatePreset('all');
          setFilterDateFrom('');
          setFilterDateTo('');
        }}
      />

      {selectedClub ? (
        <MatchupHighlights
          bestWith={highlightLists.bestWith}
          bestAgainst={highlightLists.bestAgainst}
          minGames={highlightMinGames}
          onMinGamesChange={setHighlightMinGames}
          isLoading={isLoadingHighlights}
          hasSelections={hasSelections}
          onSelectTeammate={handleSelectHighlightTeammate}
          onSelectOpponent={handleSelectHighlightOpponent}
        />
      ) : null}

      <MatchupPlayerPicker
        teammates={teammates}
        opponents={opponents}
        onAddTeammate={handleAddTeammate}
        onRemoveTeammate={handleRemoveTeammate}
        onAddOpponent={handleAddOpponent}
        onRemoveOpponent={handleRemoveOpponent}
        getAvailableTeammates={getAvailableTeammates}
        getAvailableOpponents={getAvailableOpponents}
        getPlayerDisplayName={getPlayerDisplayName}
        isLoadingPlayers={isLoadingPlayers}
        hasSelections={hasSelections}
        resultsDescription={hasSelections ? resultsDescription : ''}
        isLoadingSummary={hasSelections && isLoadingResults}
        summary={
          statsProps ? <MatchupStatsHero embedded {...statsProps} /> : null
        }
      />

      {hasSelections && !isLoadingResults && results && (
        <div className="space-y-6">
            <ResultsDisplay
              results={results}
              resultsMode={resultsMode}
              currentPlayerId={currentPlayerId}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              showCloseGamesOnly={showCloseGamesOnly}
              setShowCloseGamesOnly={setShowCloseGamesOnly}
              getSortedAndFilteredGames={getSortedAndFilteredGames}
              getPlayerDisplayName={getPlayerDisplayName}
            />
        </div>
      )}
      {hasSelections && !isLoadingResults && !results && (
        <div className="text-center py-8 text-text-muted">No results found</div>
      )}
    </div>
  );
};

const Tag = ({ player, type, onRemove }) => {
  const isTeammate = type === 'teammate';
  const bgColor = isTeammate ? 'bg-success-light text-success-dark border-success' : 'bg-info-light text-info-dark border-info';
  
  const playerName = player.first_name && player.last_name
    ? `${player.first_name} ${player.last_name}`
    : (player.name || 'Unknown Player');
  
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${bgColor}`}>
      {playerName}
      <button
        onClick={() => onRemove(player.id)}
        className="hover:opacity-70 focus:outline-none"
        aria-label={`Remove ${playerName}`}
      >
        ×
      </button>
    </span>
  );
};

/**
 * Results Display Component
 */
const ResultsDisplay = ({
  results,
  resultsMode,
  currentPlayerId,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  showCloseGamesOnly,
  setShowCloseGamesOnly,
  getSortedAndFilteredGames,
  getPlayerDisplayName
}) => {
  if (resultsMode === 'teammate-only') {
    return <TeammateOnlyDisplay results={results} currentPlayerId={currentPlayerId} />;
  } else if (resultsMode === 'individual') {
    return (
      <IndividualH2HDisplay
        h2hData={results}
        currentPlayerId={currentPlayerId}
        opponent={results.opponent}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        showCloseGamesOnly={showCloseGamesOnly}
        setShowCloseGamesOnly={setShowCloseGamesOnly}
        getSortedAndFilteredGames={getSortedAndFilteredGames}
        getPlayerDisplayName={getPlayerDisplayName}
      />
    );
  } else if (resultsMode === 'opponent-pair' || resultsMode === 'team-vs-team') {
    return (
      <TeamH2HDisplay
        h2hData={results}
        opponent={results.opponent || { name: results.opponents?.map(o => getPlayerDisplayName(o)).join(' & ') || 'Opponents' }}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        showCloseGamesOnly={showCloseGamesOnly}
        setShowCloseGamesOnly={setShowCloseGamesOnly}
        getSortedAndFilteredGames={getSortedAndFilteredGames}
        currentPlayerId={currentPlayerId}
        resultsMode={resultsMode}
      />
    );
  }
  
  return null;
};

/**
 * Transform head-to-head game data to GameHistoryTable format
 */
const transformGameForHistoryTable = (game, currentPlayerId, mode, opponent = null) => {
  // Extract score first - check multiple possible formats
  let team1Score = 0;
  let team2Score = 0;
  
  if (game.score && typeof game.score === 'string') {
    const parts = game.score.split('-');
    if (parts.length === 2) {
      const score1 = parseInt(parts[0].trim());
      const score2 = parseInt(parts[1].trim());
      if (!isNaN(score1) && !isNaN(score2)) {
        team1Score = score1;
        team2Score = score2;
      }
    }
  } else if (game.team1Score !== undefined && game.team2Score !== undefined) {
    team1Score = game.team1Score;
    team2Score = game.team2Score;
  } else if (game.team_1_score !== undefined && game.team_2_score !== undefined) {
    team1Score = game.team_1_score;
    team2Score = game.team_2_score;
  } else if (game.session_games?.team_1_score !== undefined && game.session_games?.team_2_score !== undefined) {
    team1Score = game.session_games.team_1_score;
    team2Score = game.session_games.team_2_score;
  }

  // Base structure - GameHistoryTable expects session_games nested object
  const transformed = {
    id: game.gameId || game.id,
    created_at: game.date || game.created_at || game.createdAt,
    session_id: game.sessionId || game.session_id,
    player_id: currentPlayerId,
    club_id: game.club_id || game.clubId,
    clubName: game.clubName || game.club_name,
    pair_type: game.pairType || game.pair_type || 'doubles',
    game_type: game.game_type || 'competitive',
    eloChange: game.eloChange || game.player1EloChange || game.player2EloChange || game.team1EloChange || null,
    // GameHistoryTable expects scores in session_games nested object
    session_games: {
      team_1_score: team1Score || 0,
      team_2_score: team2Score || 0,
      created_at: game.date || game.created_at || game.createdAt,
      session_id: game.sessionId || game.session_id
    },
    // Also include directly for fallback
    team_1_score: team1Score || 0,
    team_2_score: team2Score || 0
  };

  // Helper function to normalize player objects to have first_name/last_name format
  // fetchPlayerDetails returns firstName/lastName, but GameHistoryTable expects first_name/last_name
  const normalizePlayer = (player) => {
    if (!player) return null;
    const firstName = (player.first_name ?? player.firstName ?? '').trim();
    const lastName = (player.last_name ?? player.lastName ?? '').trim();
    const name = (player.name || `${firstName} ${lastName}`.trim() || firstName || '').trim();
    if (!player.id || !name) return null;
    return {
      id: player.id,
      first_name: firstName,
      last_name: lastName,
      name,
      gender: player.gender
    };
  };
  
  // Determine which team the current player is on and set team_number
  // GameHistoryTable uses team_number to determine score display
  let currentPlayerTeamNumber = 1; // Default to team 1
  
  // Transform based on mode
  if (mode === 'individual') {
    const isCurrentUserPlayer1 = game.player1Id === currentPlayerId;
    transformed.result = isCurrentUserPlayer1 ? game.player1Result : game.player2Result;
    currentPlayerTeamNumber = isCurrentUserPlayer1 ? game.player1TeamNumber : game.player2TeamNumber;
    transformed.team_number = currentPlayerTeamNumber || 1;
    
    transformed.teammates = isCurrentUserPlayer1 
      ? [normalizePlayer(game.player1Teammate)].filter(Boolean) 
      : [normalizePlayer(game.player2Teammate)].filter(Boolean);
    
    // For individual H2H, opponents should include the opponent player and their teammate
    // player1Teammate and player2Teammate are already full player objects from fetchPlayerDetails
    if (isCurrentUserPlayer1) {
      // Current user is player1, opponent is player2 and their teammate
      // Use opponent object if provided (from component selection), otherwise create from game data
      const opponentPlayer = opponent ? {
        id: opponent.id,
        first_name: opponent.first_name || opponent.firstName,
        last_name: opponent.last_name || opponent.lastName,
        name: opponent.name || `${opponent.first_name || opponent.firstName} ${opponent.last_name || opponent.lastName}`,
        gender: opponent.gender
      } : null;
      const opponentTeammate = normalizePlayer(game.player2Teammate);
      // Combine into array
      transformed.opponents = [opponentPlayer, opponentTeammate].filter(o => o && o.id);
    } else {
      // Current user is player2, opponent is player1 and their teammate
      const opponentPlayer = opponent ? {
        id: opponent.id,
        first_name: opponent.first_name || opponent.firstName,
        last_name: opponent.last_name || opponent.lastName,
        name: opponent.name || `${opponent.first_name || opponent.firstName} ${opponent.last_name || opponent.lastName}`,
        gender: opponent.gender
      } : null;
      const opponentTeammate = normalizePlayer(game.player1Teammate);
      transformed.opponents = [opponentPlayer, opponentTeammate].filter(o => o && o.id);
    }
    
    // Set ELO change for individual mode
    transformed.eloChange = isCurrentUserPlayer1 ? game.player1EloChange : game.player2EloChange;
  } else if (mode === 'team-vs-team' || mode === 'opponent-pair') {
    transformed.result = game.team1Result === 'win' ? 'win' : 'loss';
    currentPlayerTeamNumber = game.team1PlayersInGameTeamNumber || 1;
    transformed.team_number = currentPlayerTeamNumber;
    transformed.teammates = [normalizePlayer(game.team1Player1), normalizePlayer(game.team1Player2)].filter(Boolean);
    transformed.opponents = [normalizePlayer(game.team2Player1), normalizePlayer(game.team2Player2)].filter(Boolean);
  } else if (mode === 'teammate-only') {
    transformed.result = game.isWin ? 'win' : 'loss';
    currentPlayerTeamNumber = game.team1PlayersInGameTeamNumber || 1;
    transformed.team_number = currentPlayerTeamNumber;
    transformed.teammates = game.team1Player1 && game.team1Player2 
      ? [normalizePlayer(game.team1Player1), normalizePlayer(game.team1Player2)].filter(Boolean)
      : [];
    transformed.opponents = game.team2Player1 && game.team2Player2
      ? [normalizePlayer(game.team2Player1), normalizePlayer(game.team2Player2)].filter(Boolean)
      : [];
  }
  
  // Update session_games with correct scores
  // GameHistoryTable uses team_number to determine which score is the user's
  // So we keep scores as-is (team1 vs team2) and let GameHistoryTable handle the display
  if (team1Score !== null && team2Score !== null) {
    transformed.session_games.team_1_score = team1Score;
    transformed.session_games.team_2_score = team2Score;
    transformed.team_1_score = team1Score;
    transformed.team_2_score = team2Score;
  }

  return transformed;
};

/**
 * Teammate Only Display (Partnership Stats)
 */
const TeammateOnlyDisplay = ({ results, currentPlayerId }) => {
  const { teammate, totalGames, wins, losses, winRate, games, recentResults } = results;
  const [sessionNames, setSessionNames] = React.useState({});
  const [clubNames, setClubNames] = React.useState({});
  
  // Fetch session names and club names for all unique IDs
  React.useEffect(() => {
    const fetchNames = async () => {
      if (!games || games.length === 0) return;
      
      // Try multiple ways to extract session_id from games
      const sessionIds = [...new Set(games.map(g => {
        if (g.sessionId) return g.sessionId;
        if (g.session_id) return g.session_id;
        if (g.session_games?.session_id) return g.session_games.session_id;
        return null;
      }).filter(Boolean))];
      
      // Extract club IDs from games
      const clubIds = [...new Set(games.map(g => {
        if (g.club_id) return g.club_id;
        if (g.clubId) return g.clubId;
        return null;
      }).filter(Boolean))];
      
      // Fetch session names
      if (sessionIds.length > 0) {
        try {
          const { data: sessions, error } = await supabase
            .from('sessions')
            .select('id, name')
            .in('id', sessionIds);
          
          if (error) {
            console.error('Error fetching session names:', error);
          } else {
            const namesMap = {};
            sessions?.forEach(session => {
              if (session && session.id) {
                namesMap[session.id] = session.name;
              }
            });
            setSessionNames(namesMap);
          }
        } catch (err) {
          console.error('Error fetching session names:', err);
        }
      }
      
      // Fetch club names
      if (clubIds.length > 0) {
        try {
          const { data: clubs, error } = await supabase
            .from('clubs')
            .select('id, name')
            .in('id', clubIds);
          
          if (error) {
            console.error('Error fetching club names:', error);
          } else {
            const namesMap = {};
            clubs?.forEach(club => {
              if (club && club.id) {
                namesMap[club.id] = club.name;
              }
            });
            setClubNames(namesMap);
          }
        } catch (err) {
          console.error('Error fetching club names:', err);
        }
      }
    };
    
    fetchNames();
  }, [games]);
  
  // Transform games for GameHistoryTable
  const transformedGames = React.useMemo(() => {
    if (!games || games.length === 0) return [];
    
    return games.map(game => {
      const transformed = transformGameForHistoryTable(game, currentPlayerId, 'teammate-only');
      // Add session name if available
      const sessionId = game.sessionId || game.session_id || transformed.session_id;
      if (sessionId && sessionNames[sessionId]) {
        transformed.sessionName = sessionNames[sessionId];
      }
      // Add club name if available
      const clubId = game.club_id || game.clubId || transformed.club_id;
      if (clubId && clubNames[clubId]) {
        transformed.clubName = clubNames[clubId];
      }
      return transformed;
    });
  }, [games, currentPlayerId, sessionNames, clubNames]);
  
  return (
    <div className="space-y-6">
      {/* Recent Form */}
      {recentResults && recentResults.length > 0 && (
        <RecentForm 
          results={recentResults}
          variant="compact"
          title="Recent Form"
          showLatestIndicator={true}
          showLegend={false}
          showSummary={false}
          maxGames={5}
        />
      )}

      {/* Games List */}
      {transformedGames && transformedGames.length > 0 && (
        <GameHistoryTable 
          games={transformedGames} 
          pageSize={10}
          hideFilters={true}
          hideSessionColumn={true}
              />
      )}
    </div>
  );
};

/**
 * Individual H2H Display (reused from original component)
 */
const IndividualH2HDisplay = ({ h2hData, currentPlayerId, opponent, sortBy, setSortBy, sortOrder, setSortOrder, showCloseGamesOnly, setShowCloseGamesOnly, getSortedAndFilteredGames, getPlayerDisplayName }) => {
  const { player1Wins, player2Wins, player1WinRate, player2WinRate, recentResults, games } = h2hData;
  const [sessionNames, setSessionNames] = React.useState({});
  const [clubNames, setClubNames] = React.useState({});
  
  // Fetch session names and club names for all unique IDs
  React.useEffect(() => {
    const fetchNames = async () => {
      if (!games || games.length === 0) return;
      
      // Try multiple ways to extract session_id from games
      const sessionIds = [...new Set(games.map(g => {
        if (g.sessionId) return g.sessionId;
        if (g.session_id) return g.session_id;
        if (g.session_games?.session_id) return g.session_games.session_id;
        return null;
      }).filter(Boolean))];
      
      // Extract club IDs from games
      const clubIds = [...new Set(games.map(g => {
        if (g.club_id) return g.club_id;
        if (g.clubId) return g.clubId;
        return null;
      }).filter(Boolean))];
      
      // Fetch session names
      if (sessionIds.length > 0) {
        try {
          const { data: sessions, error } = await supabase
            .from('sessions')
            .select('id, name')
            .in('id', sessionIds);
          
          if (error) {
            console.error('Error fetching session names:', error);
          } else {
            const namesMap = {};
            sessions?.forEach(session => {
              if (session && session.id) {
                namesMap[session.id] = session.name;
              }
            });
            setSessionNames(namesMap);
          }
        } catch (err) {
          console.error('Error fetching session names:', err);
        }
      }
      
      // Fetch club names
      if (clubIds.length > 0) {
        try {
          const { data: clubs, error } = await supabase
            .from('clubs')
            .select('id, name')
            .in('id', clubIds);
          
          if (error) {
            console.error('Error fetching club names:', error);
          } else {
            const namesMap = {};
            clubs?.forEach(club => {
              if (club && club.id) {
                namesMap[club.id] = club.name;
              }
            });
            setClubNames(namesMap);
          }
        } catch (err) {
          console.error('Error fetching club names:', err);
        }
      }
    };
    
    fetchNames();
  }, [games]);
  
  // Transform games for GameHistoryTable
  const transformedGames = React.useMemo(() => {
    if (!games || games.length === 0) return [];
    
    return games.map(game => {
      const transformed = transformGameForHistoryTable(game, currentPlayerId, 'individual', opponent);
      // Add session name if available
      const sessionId = game.sessionId || game.session_id || transformed.session_id;
      if (sessionId && sessionNames[sessionId]) {
        transformed.sessionName = sessionNames[sessionId];
      }
      // Add club name if available
      const clubId = game.club_id || game.clubId || transformed.club_id;
      if (clubId && clubNames[clubId]) {
        transformed.clubName = clubNames[clubId];
      }
      return transformed;
    });
  }, [games, currentPlayerId, opponent, sessionNames, clubNames]);
  
  return (
    <div className="space-y-6">
      {recentResults && recentResults.length > 0 && (
        <RecentForm 
          results={recentResults}
          variant="compact"
          title="Recent Form (Last 5 games)"
          showLatestIndicator={true}
          showLegend={false}
          showSummary={false}
          maxGames={5}
        />
      )}

      {/* Game History */}
      {transformedGames && transformedGames.length > 0 && (
        <GameHistoryTable 
          games={transformedGames} 
          pageSize={10}
          hideFilters={true}
          hideSessionColumn={true}
              />
      )}
    </div>
  );
};

/**
 * Team H2H Display (reused from original component)
 */
const TeamH2HDisplay = ({ h2hData, opponent, sortBy, setSortBy, sortOrder, setSortOrder, showCloseGamesOnly, setShowCloseGamesOnly, getSortedAndFilteredGames, currentPlayerId, resultsMode }) => {
  const { team1Wins = 0, team2Wins = 0, team1WinRate = 0, team2WinRate = 0, games = [], recentResults = [], totalGames = 0, draws = 0 } = h2hData || {};
  const [sessionNames, setSessionNames] = React.useState({});
  const [clubNames, setClubNames] = React.useState({});
  
  // Fetch session names and club names for all unique IDs
  React.useEffect(() => {
    const fetchNames = async () => {
      if (!games || games.length === 0) return;
      
      // Try multiple ways to extract session_id from games
      const sessionIds = [...new Set(games.map(g => {
        if (g.sessionId) return g.sessionId;
        if (g.session_id) return g.session_id;
        if (g.session_games?.session_id) return g.session_games.session_id;
        return null;
      }).filter(Boolean))];
      
      // Extract club IDs from games
      const clubIds = [...new Set(games.map(g => {
        if (g.club_id) return g.club_id;
        if (g.clubId) return g.clubId;
        return null;
      }).filter(Boolean))];
      
      // Fetch session names
      if (sessionIds.length > 0) {
        try {
          const { data: sessions, error } = await supabase
            .from('sessions')
            .select('id, name')
            .in('id', sessionIds);
          
          if (error) {
            console.error('Error fetching session names:', error);
          } else {
            const namesMap = {};
            sessions?.forEach(session => {
              if (session && session.id) {
                namesMap[session.id] = session.name;
              }
            });
            setSessionNames(namesMap);
          }
        } catch (err) {
          console.error('Error fetching session names:', err);
        }
      }
      
      // Fetch club names
      if (clubIds.length > 0) {
        try {
          const { data: clubs, error } = await supabase
            .from('clubs')
            .select('id, name')
            .in('id', clubIds);
          
          if (error) {
            console.error('Error fetching club names:', error);
          } else {
            const namesMap = {};
            clubs?.forEach(club => {
              if (club && club.id) {
                namesMap[club.id] = club.name;
              }
            });
            setClubNames(namesMap);
          }
        } catch (err) {
          console.error('Error fetching club names:', err);
        }
      }
    };
    
    fetchNames();
  }, [games]);
  
  // Transform games for GameHistoryTable
  const transformedGames = React.useMemo(() => {
    if (!games || games.length === 0) return [];
    
    return games.map(game => {
      const transformed = transformGameForHistoryTable(game, currentPlayerId, resultsMode);
      // Add session name if available
      const sessionId = game.sessionId || game.session_id || transformed.session_id;
      if (sessionId && sessionNames[sessionId]) {
        transformed.sessionName = sessionNames[sessionId];
      }
      // Add club name if available
      const clubId = game.club_id || game.clubId || transformed.club_id;
      if (clubId && clubNames[clubId]) {
        transformed.clubName = clubNames[clubId];
      }
      return transformed;
    });
  }, [games, currentPlayerId, resultsMode, sessionNames, clubNames]);

  // Calculate actual totals from games if available, otherwise use provided stats
  const actualTotalGames = totalGames || games.length;
  const actualTeam1Wins = team1Wins || games.filter(g => g.team1Result === 'win' || (g.winningTeam === g.team1PlayersInGameTeamNumber)).length;
  const actualTeam2Wins = team2Wins || games.filter(g => g.team1Result === 'loss' || (g.winningTeam !== g.team1PlayersInGameTeamNumber && g.winningTeam !== null)).length;
  return (
    <div className="space-y-6">
      {recentResults && recentResults.length > 0 && (
        <RecentForm 
          results={recentResults}
          variant="minimal"
          title="Recent Form"
          showLatestIndicator={true}
          showLegend={false}
          showSummary={false}
          maxGames={5}
        />
      )}

      {/* Game History */}
      {transformedGames && transformedGames.length > 0 && (
        <GameHistoryTable 
          games={transformedGames} 
          pageSize={10}
          hideFilters={true}
          hideSessionColumn={true}
              />
      )}

      {(!games || games.length === 0) && (
        <div className="text-center py-8 text-text-muted">
          <div className="text-4xl mb-4">🏸</div>
          <p>No games found between these teams</p>
        </div>
      )}
    </div>
  );
};

export default UnifiedHeadToHead;

